import { useState, useRef, useEffect } from 'react'
import { Loader2, Download, Copy, Trash2, Wand2, AlertTriangle, Maximize2, Minimize2 } from 'lucide-react'
import toast from 'react-hot-toast'
import client from '../api/client'

interface CanvasPreviewProps {
  token: string | null
  selectedModel?: string
}

export default function CanvasPreview({ token, selectedModel }: CanvasPreviewProps) {
  const [prompt, setPrompt] = useState('')
  const [html, setHtml] = useState('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fullscreen, setFullscreen] = useState(false)
  const [tokensSpent, setTokensSpent] = useState(0)
  const [mobileTab, setMobileTab] = useState<'editor' | 'preview'>('editor')
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)
  const iframeRef = useRef<HTMLIFrameElement>(null)
  const canvasRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [])

  const isMobile = windowWidth < 1024

  const handleGenerate = async () => {
    if (!prompt.trim() || isGenerating) return
    setIsGenerating(true)
    setError(null)
    setHtml('')
    setTokensSpent(0)

    try {
      const res = await client.post('/code/generate-page', { prompt: prompt.trim(), model: selectedModel || undefined })
      const data = res.data

      if (data.error) {
        setError(data.error)
        toast.error(data.error)
        return
      }

      setHtml(data.html)
      setTokensSpent(data.tokens_spent || 0)
      if (data.tokens_spent > 0) {
        toast.success(`-${data.tokens_spent} CN ⚡`)
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Ошибка генерации'
      setError(msg)
      toast.error(msg)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopy = () => {
    if (!html) return
    try {
      navigator.clipboard.writeText(html)
      toast.success('HTML скопирован')
    } catch {
      toast.error('Не удалось скопировать')
    }
  }

  const handleDownload = () => {
    if (!html) return
    const blob = new Blob([html], { type: 'text/html' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'page.html'
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Скачано page.html')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleGenerate()
    }
  }

  const examples = [
    'красивый таймер с анимацией цифр',
    'интерактивный TODO-лист с drag-and-drop',
    'дэшборд погоды с иконками',
    'счётчик кликов с прогресс-баром',
    'анимированная карточка товара',
  ]

  return (
    <div ref={canvasRef} className={`flex flex-col ${fullscreen ? 'fixed inset-0 z-50 bg-[#0a0a15]' : 'flex-1'}`} style={{ minHeight: 0 }}>
      {/* Top bar */}
      <div className="flex items-center gap-2 px-3 py-1.5 border-b border-white/5 bg-[#0f0f1a] shrink-0">
        <Wand2 className="w-3.5 h-3.5 text-purple-400" />
        <span className="text-xs text-gray-300 font-medium">🎨 Канвас</span>
        <span className="text-[10px] text-gray-600">— AI генерирует HTML/CSS/JS с предпросмотром</span>
        <div className="ml-auto flex items-center gap-1">
          {tokensSpent > 0 && (
            <span className="text-[10px] text-gray-500">-{tokensSpent} CN</span>
          )}
          {html && (
            <>
              <button onClick={handleCopy}
                className="p-1.5 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Копировать HTML">
                <Copy className="w-3.5 h-3.5" />
              </button>
              <button onClick={handleDownload}
                className="p-1.5 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title="Скачать .html">
                <Download className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => setFullscreen(!fullscreen)}
                className="p-1.5 rounded hover:bg-white/5 text-gray-500 hover:text-gray-300 transition-colors" title={fullscreen ? 'Свернуть' : 'На весь экран'}>
                {fullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
              </button>
              <button onClick={() => { setHtml(''); setError(null); setTokensSpent(0) }}
                className="p-1.5 rounded hover:bg-white/5 text-gray-500 hover:text-red-400 transition-colors" title="Очистить">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </>
          )}
        </div>
      </div>

      {/* Mobile tab bar */}
      {isMobile && (
        <div className="flex items-center gap-0.5 px-4 py-1.5 border-b border-white/5 bg-[#0d0d1a] shrink-0">
          <button
            onClick={() => setMobileTab('editor')}
            className={`px-3 py-1 rounded text-[11px] transition-colors ${mobileTab === 'editor' ? 'bg-purple-500/15 text-purple-300' : 'text-gray-500 hover:text-gray-300'}`}
          >
            ✏️ Редактор
          </button>
          {html && (
            <button
              onClick={() => setMobileTab('preview')}
              className={`px-3 py-1 rounded text-[11px] transition-colors ${mobileTab === 'preview' ? 'bg-purple-500/15 text-purple-300' : 'text-gray-500 hover:text-gray-300'}`}
            >
              👁️ Предпросмотр
            </button>
          )}
        </div>
      )}

      {/* Split view */}
      <div className="flex flex-1" style={{ minHeight: 0 }}>
        {/* LEFT: prompt + code */}
        <div className="flex flex-col border-r border-white/5" style={{ minHeight: 0, width: isMobile ? '100%' : '50%', display: isMobile && mobileTab === 'preview' ? 'none' : undefined }}>
          {/* Prompt input */}
          <div className="shrink-0 px-3 py-2 border-b border-white/5 bg-[#0d0d1a]">
            <label className="text-[10px] text-gray-500 mb-1.5 block">Промпт для генерации страницы</label>
            <div className="flex gap-1.5">
              <textarea
                value={prompt}
                onChange={e => setPrompt(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Напишите, что хотите увидеть…"
                className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-xs text-gray-300 placeholder-gray-600 resize-none focus:outline-none focus:border-purple-500/30 min-h-[38px] max-h-[120px]"
                rows={2}
              />
              <button
                onClick={handleGenerate}
                disabled={isGenerating || !prompt.trim()}
                className="shrink-0 px-3 py-2 rounded-xl bg-purple-500/20 border border-purple-500/30 text-purple-400 text-xs hover:bg-purple-500/30 transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                {isGenerating ? (
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                ) : (
                  <Wand2 className="w-3.5 h-3.5" />
                )}
                {isGenerating ? 'Генерация…' : 'Сгенерировать'}
              </button>
            </div>
            {/* Examples */}
            {!html && !prompt && (
              <div className="flex flex-wrap gap-1 mt-2">
                {examples.map((ex, i) => (
                  <button
                    key={i}
                    onClick={() => setPrompt(ex)}
                    className="text-[10px] px-2 py-0.5 rounded-full bg-white/5 border border-white/5 text-gray-500 hover:text-purple-400 hover:border-purple-500/20 transition-colors"
                  >
                    {ex}
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Generated code */}
          <div className="flex-1 overflow-hidden relative" style={{ minHeight: 0 }}>
            {!html && !error && !isGenerating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 text-center">
                <Wand2 className="w-8 h-8 text-purple-500/20 mb-2" />
                <p className="text-[11px] text-gray-600 max-w-[250px] leading-relaxed">
                  Опишите, какую страницу хотите, и AI сгенерирует готовый HTML/CSS/JS
                </p>
                <div className="mt-3 rounded-lg border border-white/5 bg-[#0a0a15] p-2.5 max-w-[300px]">
                  <pre className="text-[10px] text-gray-500 font-mono leading-relaxed whitespace-pre-wrap text-left">{`сделай красивый таймер
с анимацией цифр,
градиентным фоном
и тенями`}</pre>
                </div>
              </div>
            )}
            {isGenerating && (
              <div className="absolute inset-0 flex flex-col items-center justify-center p-4 bg-[#0d0d1a]/80">
                <div className="relative w-12 h-12 mb-3">
                  <div className="absolute inset-0 rounded-full border-2 border-purple-500/20 animate-ping" />
                  <div className="absolute inset-1 rounded-full border-2 border-transparent border-t-purple-500 animate-spin" />
                </div>
                <p className="text-xs text-gray-500 animate-pulse">AI генерирует страницу…</p>
                <p className="text-[10px] text-gray-600 mt-1">Это может занять до 30 секунд</p>
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex items-center justify-center p-4">
                <div className="text-center">
                  <AlertTriangle className="w-8 h-8 text-red-400/50 mx-auto mb-2" />
                  <p className="text-xs text-red-400">{error}</p>
                  <button
                    onClick={handleGenerate}
                    className="mt-2 px-3 py-1 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400 hover:bg-red-500/20 transition-colors"
                  >
                    Повторить
                  </button>
                </div>
              </div>
            )}
            {html && !isGenerating && (
              <div className="h-full overflow-auto">
                <div className="px-3 py-1.5 bg-[#151525] border-b border-white/5 sticky top-0">
                  <span className="text-[10px] text-gray-500 font-mono">index.html</span>
                </div>
                <pre className="p-3 text-[11px] text-gray-300 font-mono leading-relaxed whitespace-pre-wrap overflow-auto">
                  <code>{html}</code>
                </pre>
              </div>
            )}
          </div>
        </div>

        {/* RIGHT: preview */}
        <div className="flex flex-col" style={{ minHeight: 0, width: isMobile ? '100%' : '50%', display: isMobile && mobileTab === 'editor' ? 'none' : undefined }}>
          <div className="shrink-0 px-3 py-1.5 border-b border-white/5 bg-[#0f0f1a] flex items-center justify-between">
            <span className="text-[10px] text-gray-500 font-mono">Предпросмотр</span>
            {html && (
              <div className="flex items-center gap-2 text-[10px] text-gray-600">
                <span>🖥️ песочница (sandbox)</span>
              </div>
            )}
          </div>
          <div className="flex-1 bg-white relative overflow-hidden" style={{ minHeight: 0 }}>
            {html ? (
              <iframe
                ref={iframeRef}
                srcDoc={html}
                sandbox="allow-scripts allow-forms allow-popups"
                title="Canvas Preview"
                className="w-full h-full border-0"
                style={{ backgroundColor: '#fff' }}
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 mx-auto mb-3 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                    <Maximize2 className="w-6 h-6 text-gray-500" />
                  </div>
                  <p className="text-xs text-gray-600">Здесь появится предпросмотр</p>
                  <p className="text-[10px] text-gray-600/60 mt-1">сгенерированной страницы</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
